import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { DetailHeader } from "@/components/shared/PageChrome";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-jigsaw-muted">{label}</p>
      <p className="mt-1 text-sm text-jigsaw-navy">{value || "—"}</p>
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [error, setError] = useState(false);
  const [unitOpen, setUnitOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [unitForm, setUnitForm] = useState({ unit_type: "", capacity: 1, description: "" });
  const [rateForm, setRateForm] = useState({ unit_id: "", effective_from: "", effective_to: "", rate_amount: "", currency: "GBP", vat_treatment: "INCLUSIVE", notes: "" });
  const navigate = useNavigate();

  const load = () => api.get(`/directory/properties/${id}`).then((r) => setProperty(r.data)).catch(() => setError(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitUnit = async () => {
    try {
      await api.post(`/directory/properties/${id}/units`, { ...unitForm, capacity: Number(unitForm.capacity) });
      toast.success("Unit added");
      setUnitOpen(false);
      setUnitForm({ unit_type: "", capacity: 1, description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add unit"); }
  };

  const submitRate = async () => {
    try {
      await api.post(`/directory/properties/${id}/rate-cards`, {
        ...rateForm, unit_id: rateForm.unit_id || null, effective_to: rateForm.effective_to || null, rate_amount: Number(rateForm.rate_amount),
      });
      toast.success("Rate card added");
      setRateOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add rate card"); }
  };

  if (error) return <ErrorState testId="property-detail-error" />;
  if (!property) return <LoadingState testId="property-detail-loading" />;

  return (
    <div data-testid="property-detail-page" className="space-y-5">
      <DetailHeader
        testId="property-detail-header"
        crumbs={[{ label: "Hotels & Properties", to: "/control-centre/properties" }, { label: property.name }]}
        chips={property.property_type && <span className="rounded-full border border-jigsaw-border px-2.5 py-0.5 text-[12px] text-jigsaw-muted">{property.property_type}</span>}
        title={property.name}
        meta={[
          { label: "Address", value: property.address_line1 },
          { label: "City", value: property.city },
          { label: "Postcode", value: property.postcode, mono: true },
          { label: "Legacy reference", value: property.legacy_reference, mono: true },
        ]}
        actions={<button onClick={() => navigate("/control-centre/properties")} data-testid="property-back-button" className="sr-only">Back to Properties</button>}
      />

      <Card className="border-jigsaw-border">
        <CardHeader><CardTitle className="text-base text-jigsaw-navy">Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-5 md:grid-cols-4">
          <Field label="Address" value={property.address_line1} />
          <Field label="City" value={property.city} />
          <Field label="Postcode" value={property.postcode} />
          <Field label="Type" value={property.property_type} />
          <Field label="Legacy reference" value={property.legacy_reference} />
        </CardContent>
      </Card>

      <Card className="border-jigsaw-border">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base text-jigsaw-navy">Units</CardTitle>
          <Dialog open={unitOpen} onOpenChange={setUnitOpen}>
            <DialogTrigger asChild><Button data-testid="add-unit-button" size="sm" variant="outline"><Plus className="mr-1 h-3.5 w-3.5" /> Add Unit</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add unit</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Unit type</Label><Input data-testid="unit-type-input" value={unitForm.unit_type} onChange={(e) => setUnitForm({ ...unitForm, unit_type: e.target.value })} /></div>
                <div><Label>Capacity</Label><Input data-testid="unit-capacity-input" type="number" min={1} value={unitForm.capacity} onChange={(e) => setUnitForm({ ...unitForm, capacity: e.target.value })} /></div>
                <div><Label>Description</Label><Input value={unitForm.description} onChange={(e) => setUnitForm({ ...unitForm, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="unit-save-button" onClick={submitUnit}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-2">
          {property.units.length === 0 && <p className="text-sm text-jigsaw-muted">No units recorded yet.</p>}
          {property.units.map((u) => (
            <div key={u.id} className="flex items-center justify-between rounded-md border border-jigsaw-border px-3 py-2 text-sm">
              <span className="text-jigsaw-navy">{u.unit_type}</span>
              <span className="text-xs text-jigsaw-muted">Capacity {u.capacity}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-jigsaw-border">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base text-jigsaw-navy">Rate Cards</CardTitle>
          <Dialog open={rateOpen} onOpenChange={setRateOpen}>
            <DialogTrigger asChild><Button data-testid="add-rate-card-button" size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"><Plus className="mr-1 h-3.5 w-3.5" /> Add Rate Card</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add effective-dated rate card</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Unit (optional)</Label>
                  <Select value={rateForm.unit_id || "none"} onValueChange={(v) => setRateForm({ ...rateForm, unit_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="rate-unit-select"><SelectValue placeholder="Whole property" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Whole property</SelectItem>{property.units.map((u) => <SelectItem key={u.id} value={u.id}>{u.unit_type}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Effective from</Label><Input data-testid="rate-from-input" type="date" value={rateForm.effective_from} onChange={(e) => setRateForm({ ...rateForm, effective_from: e.target.value })} /></div>
                  <div><Label>Effective to (optional)</Label><Input type="date" value={rateForm.effective_to} onChange={(e) => setRateForm({ ...rateForm, effective_to: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Rate amount (£)</Label><Input data-testid="rate-amount-input" type="number" step="0.01" value={rateForm.rate_amount} onChange={(e) => setRateForm({ ...rateForm, rate_amount: e.target.value })} /></div>
                  <div>
                    <Label>VAT treatment</Label>
                    <Select value={rateForm.vat_treatment} onValueChange={(v) => setRateForm({ ...rateForm, vat_treatment: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="INCLUSIVE">Inclusive</SelectItem><SelectItem value="EXCLUSIVE">Exclusive</SelectItem><SelectItem value="EXEMPT">Exempt</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button data-testid="rate-save-button" onClick={submitRate}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-2">
          {property.rate_cards.length === 0 && <p className="text-sm text-jigsaw-muted">No rate cards recorded yet.</p>}
          {property.rate_cards.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-md border border-jigsaw-border px-3 py-2 text-sm">
              <span className="text-jigsaw-navy">{r.effective_from} &rarr; {r.effective_to || "ongoing"}</span>
              <span className="font-mono-ref font-semibold text-jigsaw-navy">£{Number(r.rate_amount).toFixed(2)} {r.currency}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
